import { Camera, Geometry, Line, LineBasicMaterial, Vector2, Vector3 } from 'three';
import App from './application-container';
import { get_camera_look_vector, suppress_animate, unsuppress_animate_if_ready } from './utility';
import { uil_get_edges_of_face, uil_get_vertices_of_edge, uil_is_object_visible } from './ui_logic';
import type { Edge, Face, Vertex } from '@phi/jcore';
import { mustNotDisplayEdge, mustNotDisplayFace, mustNotDisplayVertex, vertices_of_face } from './lattice';
import { edge_is_for_sketch, face_is_for_sketch, vertex_is_for_sketch } from './reference_sketches';
import { uid_select_object } from './ui_display';
import { animate } from './scene';
import { from_3d_coordinates_to_2d_screen_coordinates } from './geometry_and_math';
import { Uid_vertex } from './graph_objects';
import { project_point_on_plane } from './vector3';

/** The 0.01 is roughly the vertex radius */
const vertex_radius = 0.01;

export default class SelectionBox {
    camera: Camera;
    startPoint: Vector2;
    endPoint: Vector2;
    minx: number;
    miny: number;
    maxx: number;
    maxy: number;
    plane: null;
    plane_geometry: null;
    plane_material: null;
    selected_objects: Set<Face | Edge | Vertex>;

    constructor() {
        this.camera = uid_camera;
        this.startPoint = new Vector2();
        this.endPoint = new Vector2();
        this.minx = 0;
        this.miny = 0;
        this.maxx = 0;
        this.maxy = 0;

        this.plane = null;
        this.plane_geometry = null;
        this.plane_material = null;

        this.selected_objects = new Set();
        scene.add(uid_selection_box_raynode);
    }

    update(newStartPoint: Vector2 | null, newEndPoint: Vector2 | null) {
        if (newStartPoint)
            this.startPoint.copy(newStartPoint);

        if (newEndPoint)
            this.endPoint.copy(newEndPoint);

        this.minx = Math.min(this.startPoint.x, this.endPoint.x);
        this.maxx = Math.max(this.startPoint.x, this.endPoint.x);
        this.miny = Math.min(this.startPoint.y, this.endPoint.y);
        this.maxy = Math.max(this.startPoint.y, this.endPoint.y);
    }

    select_objects_in_box() {
        if (App.state.editing_reference_sketches)
            return;

        this.selected_objects = new Set();

        suppress_animate();
        for (const uido of uid_vertices.values()) {
            if (uid_selected_vertices.hasItem(uido.id) && !this.selected_objects.has(uido.id))
                this.selected_objects.add(uido.id);
            else if (
                !uid_selected_vertices.hasItem(uido.id)
                && uil_is_object_visible(uido.id)
                && !this.selected_objects.has(uido.id)
                && this.box_contains_vertex(uido)
            ) {
                this.selected_objects.add(uido.id);
                if (!mustNotDisplayVertex(uido.id) && !vertex_is_for_sketch(uido.id))
                    uid_select_object(uido.id, false);
            }
        }

        const face_edge_objects: Array<Face | Edge> = [];

        for (const f of uid_faces.values()) {
            if (uid_selected_faces.hasItem(f.id) && !this.selected_objects.has(f.id))
                this.selected_objects.add(f.id);
            else if (!uid_selected_faces.hasItem(f.id) &&
                uil_is_object_visible(f.id) &&
                !this.selected_objects.has(f.id) &&
                !face_edge_objects.includes(f.id)
            ) {
                const vs = vertices_of_face(f.id);
                let face_belongs = true;
                for (const vid of vs) {
                    if (!this.selected_objects.has(vid))
                        face_belongs = false;
                }
                if (face_belongs) {
                    face_edge_objects.push(f.id);
                    const es = uil_get_edges_of_face(f.id);
                    for (const eid of es) {
                        if (!uid_selected_edges.hasItem(eid) && uil_is_object_visible(eid)) {
                            if (!this.selected_objects.has(eid) && !face_edge_objects.includes(eid))
                                face_edge_objects.push(eid);
                            if (!mustNotDisplayEdge(eid) && !edge_is_for_sketch(eid))
                                uid_select_object(eid, false);
                        }
                    }
                    if (!mustNotDisplayFace(f.id) && !face_is_for_sketch(f.id))
                        uid_select_object(f.id, false);
                }
            }
        }

        for (const e of uid_edges.values()) {
            if (uid_selected_edges.hasItem(e.id) && !this.selected_objects.has(e.id))
                this.selected_objects.has(e.id);
            else if (
                !uid_selected_edges.hasItem(e.id)
                && uil_is_object_visible(e.id)
                && !this.selected_objects.has(e.id)
                && !face_edge_objects.includes(e.id)
            ) {
                const vs = uil_get_vertices_of_edge(e.id);
                let edge_belongs = true;
                for (const vid of vs) {
                    if (!this.selected_objects.has(vid))
                        edge_belongs = false;
                }
                if (edge_belongs) {
                    face_edge_objects.push(e.id);
                    if (!mustNotDisplayEdge(e.id) && !edge_is_for_sketch(e.id))
                        uid_select_object(e.id, false);
                }
            }
        }

        for (const x of face_edge_objects)
            this.selected_objects.add(x);

        let topleft = new Vector3(this.minx, this.miny, 0.9).unproject(uid_camera);
        let topright = new Vector3(this.maxx, this.miny, 0.9).unproject(uid_camera);
        let bottomleft = new Vector3(this.minx, this.maxy, 0.9).unproject(uid_camera);
        let bottomright = new Vector3(this.maxx, this.maxy, 0.9).unproject(uid_camera);

        const l = get_camera_look_vector(uid_camera);
        topleft = project_point_on_plane(topleft, uid_camera.position, l);
        topright = project_point_on_plane(topright, uid_camera.position, l);
        bottomleft = project_point_on_plane(bottomleft, uid_camera.position, l);
        bottomright = project_point_on_plane(bottomright, uid_camera.position, l);

        for (let i = uid_selection_box_raynode.children.length - 1; i >= 0; i--) {
            uid_selection_box_raynode.remove(uid_selection_box_raynode.children[i]!);
        }

        const selection_box_edge_geometry = new Geometry();
        // @ts-ignore
        selection_box_edge_geometry.dynamic = true;
        selection_box_edge_geometry.vertices = [];
        selection_box_edge_geometry.vertices.push(topleft);
        selection_box_edge_geometry.vertices.push(topright);
        selection_box_edge_geometry.vertices.push(bottomright);
        selection_box_edge_geometry.vertices.push(bottomleft);
        selection_box_edge_geometry.vertices.push(topleft);

        const selection_box_edge_material = new LineBasicMaterial({ color: 0xffb455, linewidth: 3});
        const selection_box_edge_mesh = new Line(selection_box_edge_geometry, selection_box_edge_material);

        uid_selection_box_raynode.add(selection_box_edge_mesh);

        unsuppress_animate_if_ready();
    }

    box_contains_vertex(uido: Uid_vertex): boolean {
        const pv = from_3d_coordinates_to_2d_screen_coordinates(uido.object3d!.position);
        return (
            this.minx - vertex_radius < pv.x
            && this.maxx + vertex_radius > pv.x
            && this.miny - vertex_radius < pv.y
            && this.maxy + vertex_radius > pv.y
        );
    }

    remove(): void {
        if (!uid_selection_box || !uid_selection_box_raynode)
            return;
        for (let i = uid_selection_box_raynode.children.length - 1; i >= 0; i--)
            uid_selection_box_raynode.remove(uid_selection_box_raynode.children[i]!);
        scene.remove(uid_selection_box_raynode);
        uid_selection_box = null;
        animate();
    }
}
